
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Trophy, Star } from 'lucide-react';

interface GameDrawProps {
  selectedNumbers: number[];
  betAmount: number;
  onDrawComplete: (winnings: number) => void;
}

const GameDraw = ({ selectedNumbers, betAmount, onDrawComplete }: GameDrawProps) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawnNumbers, setDrawnNumbers] = useState<number[]>([]);
  const [currentNumber, setCurrentNumber] = useState<number | null>(null);
  const [matches, setMatches] = useState(0);
  const [winnings, setWinnings] = useState(0);
  const [showResults, setShowResults] = useState(false);

  const prizeStructure = {
    6: betAmount * 1000000, // Jackpot
    5: betAmount * 5000,
    4: betAmount * 150,
    3: betAmount * 10,
    2: betAmount * 2,
    1: 0,
    0: 0
  };

  const startDraw = () => {
    setIsDrawing(true);
    setDrawnNumbers([]);
    setCurrentNumber(null);
    setMatches(0);
    setWinnings(0);
    setShowResults(false);

    // Generate winning numbers
    const winningNumbers: number[] = [];
    while (winningNumbers.length < 6) {
      const num = Math.floor(Math.random() * 45) + 1;
      if (!winningNumbers.includes(num)) {
        winningNumbers.push(num);
      }
    }

    // Animate number drawing
    let index = 0;
    const drawInterval = setInterval(() => {
      if (index < 6) {
        // Show spinning animation
        const spinInterval = setInterval(() => {
          setCurrentNumber(Math.floor(Math.random() * 45) + 1);
        }, 50);

        // Stop spinning and show actual number
        setTimeout(() => {
          clearInterval(spinInterval);
          setCurrentNumber(winningNumbers[index]);
          setDrawnNumbers(prev => [...prev, winningNumbers[index]]);
          index++;
        }, 1000);
      } else {
        clearInterval(drawInterval);
        
        // Calculate matches and winnings
        setTimeout(() => {
          const matchCount = selectedNumbers.filter(num => 
            winningNumbers.includes(num)
          ).length;
          
          setMatches(matchCount);
          const prize = prizeStructure[matchCount as keyof typeof prizeStructure];
          setWinnings(prize);
          setIsDrawing(false);
          setShowResults(true);
          onDrawComplete(prize);
        }, 500);
      }
    }, 1500);
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2">
          <Star className="w-6 h-6 text-yellow-600" />
          Live Draw
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Your Numbers */}
        <div>
          <h3 className="font-bold text-gray-900 mb-3">Your Numbers:</h3>
          <div className="flex flex-wrap gap-2 justify-center">
            {selectedNumbers.map((num, idx) => (
              <div
                key={idx}
                className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold ${
                  showResults && drawnNumbers.includes(num)
                    ? 'bg-green-500 text-white animate-pulse'
                    : 'bg-yellow-600 text-white'
                }`}
              >
                {num}
              </div>
            ))}
          </div>
        </div>

        {/* Current Drawing */}
        {isDrawing && (
          <div className="text-center">
            <h3 className="font-bold text-gray-900 mb-3">Drawing Number...</h3>
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto animate-spin">
              {currentNumber && (
                <span className="text-2xl font-bold text-gray-900">
                  {currentNumber}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Drawn Numbers */}
        {drawnNumbers.length > 0 && (
          <div>
            <h3 className="font-bold text-gray-900 mb-3">Winning Numbers:</h3>
            <div className="flex flex-wrap gap-2 justify-center">
              {drawnNumbers.map((num, idx) => (
                <div
                  key={idx}
                  className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold animate-bounce"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  {num}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {showResults && (
          <div className="text-center space-y-4">
            <div className="bg-yellow-50 rounded-lg p-6">
              <Trophy className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {matches} Numbers Matched!
              </h3>
              <p className="text-3xl font-bold text-green-600">
                You won: ${winnings.toLocaleString()}
              </p>
              {matches >= 3 && (
                <p className="text-sm text-gray-600 mt-2">
                  Congratulations! Your prize will be credited to your account.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Draw Button */}
        {!isDrawing && !showResults && (
          <div className="text-center">
            <Button
              onClick={startDraw}
              className="bg-yellow-600 hover:bg-yellow-700 px-8 py-3"
            >
              Start Draw
            </Button>
          </div>
        )}

        {showResults && (
          <div className="text-center">
            <Button
              onClick={() => window.location.reload()}
              className="bg-yellow-600 hover:bg-yellow-700 px-8 py-3"
            >
              Play Again
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GameDraw;
